import { useCallback, useState } from "react";

const STORAGE_KEY = "staff-ui:sidebar-collapsed";

/**
 * Reads the persisted preference. Storage can throw outright in private mode or
 * when site data is blocked, so every access is guarded — a shell that cannot
 * render because localStorage is unavailable would be a far worse bug than
 * forgetting a collapse preference.
 */
function readStored(): boolean {
  try {
    return window.localStorage.getItem(STORAGE_KEY) === "true";
  } catch {
    return false;
  }
}

function writeStored(collapsed: boolean): void {
  try {
    window.localStorage.setItem(STORAGE_KEY, String(collapsed));
  } catch {
    // Preference is best-effort; the sidebar still works for this session.
  }
}

/** Collapsed state for the desktop rail, persisted per browser. */
export function useSidebarCollapsed(): {
  collapsed: boolean;
  toggle: () => void;
  expand: () => void;
} {
  const [collapsed, setCollapsed] = useState(readStored);

  const toggle = useCallback(() => {
    setCollapsed(prev => {
      const next = !prev;
      writeStored(next);
      return next;
    });
  }, []);

  const expand = useCallback(() => {
    setCollapsed(prev => {
      if (!prev) return prev;
      writeStored(false);
      return false;
    });
  }, []);

  return { collapsed, toggle, expand };
}
